import { Checkbox, FormControlLabel } from "@mui/material";
import { FormikProps } from "formik";
import Flex from "../../../../../../../../../components/Flex";
import Text from "../../../../../../../../../components/Text";
import PsychoeducationTherapyForm from "./PsychoeducationTherapyForm";
import MicroTextArea from "../../../../../../../../../components/MicroTextArea";
import { useReduceDisclosure } from "../../../../../../../../../hooks/use-reduce-disclosure";
import { StyleAddButton } from "../../../../../../scheduling/appointment/encounter/default-forms/form-1/StyledAddButton";
import { Form2Payload } from "../../../../../../scheduling/appointment/encounter/encounter-constant";
// import { useQueryParams } from "../../../../../../../../../hooks/use-query-param";

import CustomTitle from "../../../../../../../../../components/app/CustomTitle";
import { useRoleController } from "../../../../../../../../../components/useRoleController";
import NewAppointment from "../../../../../../scheduling/appointment/NewAppointment";
import {
  AddIcdCodes,
  renderSelectedIcdCodes,
} from "../components/form-components/Add-ICD-Codes";
import PsychiatricNoteObjectives from "../components/form-components/PsychiatricNoteObjectives";
import PsychiatricNotePlan from "../components/form-components/PsychiatricNotePlan";
import PsychiatricNoteSubjective from "../components/form-components/PsychiatricNoteSubjective";

interface PsychiatricNoteProps {
  formik: FormikProps<Form2Payload>;
}

/**
 *
 * @description: Psychiatric Note Form
 *
 * @author [Rajkumar Rathod]
 * @returns
 */
const PsychiatricNote = ({ formik }: PsychiatricNoteProps) => {
  const [icdDisclosure, appointmentDisclosure] = useReduceDisclosure(2);
  const { hasUpdate: hasUpdateAppointment } = useRoleController(
    "Appointment Scheduling"
  );
  return (
    <Flex
      gap={1}
      flexDirection="column"
      p={1}
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      {/* Subjective */}
      <Flex flexDirection="column" gap={1}>
        <CustomTitle>Subjective</CustomTitle>
        <MicroTextArea
          rows={4}
          variant="outlined"
          name="subjectiveFreeNote"
          placeholder="Problems..."
          onBlur={formik.handleBlur}
          value={formik.values?.subjectiveFreeNote || ""}
          onChange={formik.handleChange}
        />
      </Flex>
      <PsychiatricNoteSubjective formik={formik} />

      {/* Objective */}
      <Flex flexDirection="column" gap={1}>
        <CustomTitle>Objective</CustomTitle>
        <MicroTextArea
          rows={4}
          variant="outlined"
          name="objectiveFreeNote"
          placeholder="Observations..."
          onBlur={formik.handleBlur}
          value={formik.values?.objectiveFreeNote || ""}
          onChange={formik.handleChange}
        />
      </Flex>

      {/* objective  */}
      <PsychiatricNoteObjectives />

      {/* Assessment */}
      {/* todo : need to add assessment free text to backend API  */}
      <Flex flexDirection="column" gap={1}>
        <CustomTitle>Assessment</CustomTitle>
        <MicroTextArea
          rows={4}
          variant="outlined"
          name="assessmentFreeNote"
          placeholder="Assessment..."
          onBlur={formik.handleBlur}
          value={formik.values?.assessmentFreeNote || ""}
          onChange={formik.handleChange}
        />

        <Flex gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
          <StyleAddButton
            w="120px"
            onClick={() => icdDisclosure.open()}
            label="Add ICD Codes"
          />
          <AddIcdCodes formik={formik} disclosureHost={icdDisclosure} />
        </Flex>
      </Flex>
      {!!formik.values?.assessmentCodes?.length && (
        <Flex flexDirection={"column"} width={"100%"} gap={1}>
          {/* <Text fontWeight={"bold"}>ICD Codes</Text> */}
          {renderSelectedIcdCodes(
            formik.values?.assessmentCodes ?? [],
            (code) => {
              formik.setFieldValue("assessmentCodes", [
                ...(formik.values?.assessmentCodes ?? []).filter(
                  (it) => it.value !== code.value
                ),
              ]);
            },
            "20vh"
          )}
        </Flex>
      )}

      {/* Plan */}
      <Flex flexDirection="column" gap={1}>
        <CustomTitle>Plan</CustomTitle>
        <MicroTextArea
          rows={4}
          variant="outlined"
          name="planFreeNote"
          placeholder="Plan..."
          onBlur={formik.handleBlur}
          value={formik.values?.planFreeNote || ""}
          onChange={formik.handleChange}
        />

        {/* plan fields */}
        <PsychiatricNotePlan formik={formik} />
      </Flex>
      {/* Follow Up & Instructions */}
      <Flex flexDirection="column" gap={1}>
        <CustomTitle>Follow Up & Instructions</CustomTitle>
        <MicroTextArea
          rows={4}
          variant="outlined"
          name="followUp"
          placeholder="Follow Up & Instructions..."
          onBlur={formik.handleBlur}
          value={formik.values?.followUp || ""}
          onChange={formik.handleChange}
        />

        {hasUpdateAppointment && (
          <Flex gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
            <StyleAddButton
              w="120px"
              onClick={() => {
                appointmentDisclosure.open();
              }}
              label="Schedule F/U Appointment"
            />
            {appointmentDisclosure.opened && (
              <NewAppointment
                enablePrefillPatientData
                opened={appointmentDisclosure.opened}
                onClose={appointmentDisclosure.close}
              />
            )}
          </Flex>
        )}
        <FormControlLabel
          control={
            <Checkbox
              name="psychoeducationSessionRequired"
              checked={!!formik.values?.psychoeducationSessionRequired}
              onChange={(e) =>
                formik.setFieldValue(
                  "psychoeducationSessionRequired",
                  e.target.checked
                )
              }
            />
          }
          label={
            <Text variant="body1" fontWeight={500}>
              Psychoeducation Session Required?
            </Text>
          }
        />
        {formik.values?.psychoeducationSessionRequired && (
          <PsychoeducationTherapyForm formik={formik} />
        )}
      </Flex>
    </Flex>
  );
};

export default PsychiatricNote;
